/**
 * Hitz wire v1 (`@hitz/game-protocol`): message names, envelope, payload shapes and strict validators.
 * Everything the platform names lives here, so a protocol change touches this file and its tests only.
 * Sources and open points: docs/HITZ_WIRE_GAP.md. Replace with the official package once we have access.
 */
export const PROTOCOL_VERSION = 1

/** host → game */
export const HOST = {
  initialize: 'host.initialize',
  attemptStarted: 'attempt.started',
  attemptStartRejected: 'attempt.start.rejected',
  attemptResumed: 'attempt.resumed',
  attemptFinalized: 'attempt.finalized'
} as const

/** game → host */
export const GAME = {
  ready: 'game.ready',
  sessionReady: 'session.ready',
  attemptStartRequested: 'attempt.start.requested',
  action: 'telemetry.action',
  checkpoint: 'telemetry.checkpoint',
  attemptSuspended: 'attempt.suspended',
  attemptCompleted: 'attempt.completed',
  attemptFailed: 'attempt.failed',
  closeRequested: 'host.close.requested',
  fullscreenRequested: 'host.fullscreen.requested',
  /** Technical diagnostic (`code`, `detail?`). Never replaces an outcome; the host treats it, the game only reports (guide §4). */
  error: 'game.error'
} as const

export type HostMessageType = (typeof HOST)[keyof typeof HOST]
export type GameMessageType = (typeof GAME)[keyof typeof GAME]

const ENVELOPE_KEYS = ['protocolVersion', 'gameId', 'gameVersion', 'sessionId', 'attemptId', 'messageId',
  'messageType', 'occurredAt', 'nonce', 'payload'] as const

export interface Envelope<T extends string = string, P = Record<string, unknown>> {
  protocolVersion: 1
  gameId: string
  gameVersion: string
  sessionId?: string
  attemptId?: string
  messageId: string
  messageType: T
  occurredAt?: string
  nonce?: string
  payload: P
}

/** Authorization carried by `attempt.started`. Operational data only: no stake, balance or odds. */
export interface Authorization {
  attemptId: string
  levelId: string
  levelVersion: string
  /** uint32: seed the rules' PRNG with it (src/core/random.ts). */
  seed: number
  rulesVersion: string
  /** 1..10000 when present (guide §6). Zero is not a valid "no limit": the field is omitted instead. */
  moveLimit?: number
  /** Play time limit, 1..86400000 ms when present. Not the inactivity timeout (that one is the platform's). */
  timeLimitMs?: number
}

export interface StartRejection { levelId: string; reasonCode: string }

/**
 * Fields of `telemetry.action`. The first five are the ones covered by the action hash. Any other scalar field
 * (`slot`, `row`, `column`, `angle`, `speed`...) travels in the payload but is NOT hashed: the backend must validate
 * it by replay/checkpoint, never by the hash alone (docs/EVENTS.md).
 */
export interface ActionFields {
  fromX?: number; fromY?: number; toX?: number; toY?: number; booster?: string
  [extra: string]: string | number | boolean | undefined
}

export interface ActionPayload extends ActionFields {
  sequence: number
  type: string
  elapsedMs?: number
  previousHash: string
  hash: string
}

/** Game-supplied part of a checkpoint; the SDK fills ids, sequence and hash. */
export interface CheckpointInput {
  boardState: string
  randomState: string
  movesRemaining: number
  score: number
  goals: string
  boosters?: string
  restorable?: boolean
}

export interface CheckpointPayload extends Required<Omit<CheckpointInput, 'boosters'>> {
  attemptId: string
  levelId: string
  levelVersion: string
  actionSequence: number
  boosters?: string
  hash: string
}

export interface OutcomePayload { levelId: string; actionSequence: number; actionHash: string; checkpointHash?: string }

// ---------------------------------------------------------------------------------------------- checks

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)
/** Non-blank string up to `max` chars (128 for ids, 64 for level/rules fields). */
export const isText = (x: unknown, max = 128): x is string => typeof x === 'string' && x.trim().length > 0 && x.length <= max
const ACTION_KEYS = ['sequence', 'type', 'fromX', 'fromY', 'toX', 'toY', 'booster', 'elapsedMs', 'previousHash', 'hash']
const EXTRA_KEY = /^[A-Za-z][A-Za-z0-9_]{0,31}$/
/** Extra action fields: short names, scalar values (string up to 128 chars, finite number or boolean). */
const extrasOk = (p: Record<string, unknown>) => Object.keys(p).every((k) => {
  if (ACTION_KEYS.includes(k)) return true
  const v = p[k]
  return EXTRA_KEY.test(k) && (typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && v.length <= 128))
})
const only = (o: Record<string, unknown>, keys: readonly string[]) => Object.keys(o).every((k) => keys.includes(k))
const isUint = (x: unknown, max = Number.MAX_SAFE_INTEGER) => Number.isInteger(x) && (x as number) >= 0 && (x as number) <= max
const optUint = (x: unknown, max = Number.MAX_SAFE_INTEGER) => x === undefined || isUint(x, max)
/** Checkpoint field limits of the canonical schema (guide §6). */
export const LIMITS = { boardState: 16384, randomState: 1024, goals: 2048, boosters: 2048, moveLimit: 10000, timeLimitMs: 86_400_000, currentLevel: 10000 } as const
const optInt = (x: unknown) => x === undefined || Number.isInteger(x)
const optText = (x: unknown, max = 64) => x === undefined || isText(x, max)
const HEX64 = /^[0-9a-f]{64}$/
const GAME_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export const isGameId = (x: unknown): x is string => typeof x === 'string' && GAME_ID.test(x)

/** `https:` origin, or `http:` on loopback only; must already be a bare origin. */
export function safeOrigin(x: unknown): string | null {
  if (typeof x !== 'string') return null
  try {
    const u = new URL(x)
    const ok = u.protocol === 'https:' || (u.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(u.hostname))
    return ok && u.origin === x ? x : null
  } catch { return null }
}

/**
 * Structural check of a host → game message: envelope keys, message type, closed payload.
 * Binding checks (origin, source, session, nonce, attempt, dedupe) are the bridge's job.
 */
export function isHostMessage(m: unknown): m is Envelope<HostMessageType> {
  if (!isObj(m) || !only(m, ENVELOPE_KEYS) || !isObj(m.payload)) return false
  if (m.protocolVersion !== PROTOCOL_VERSION || !isGameId(m.gameId) || !isText(m.gameVersion, 32) || !isText(m.messageId)) return false
  const p = m.payload
  switch (m.messageType) {
    case HOST.initialize:
      // `currentLevel` (1..10000) is allowed by the canonical schema for level titles (guide §3, passo 2); informative only.
      return m.attemptId === undefined && isText(m.sessionId) && isText(m.nonce) && (m.nonce as string).length >= 16 &&
        only(p, ['hostOrigin', 'currentLevel']) && safeOrigin(p.hostOrigin) !== null &&
        (p.currentLevel === undefined || (Number.isInteger(p.currentLevel) && (p.currentLevel as number) >= 1 && (p.currentLevel as number) <= LIMITS.currentLevel))
    case HOST.attemptStarted:
      return isText(m.attemptId) && p.attemptId === m.attemptId &&
        only(p, ['attemptId', 'levelId', 'levelVersion', 'seed', 'rulesVersion', 'moveLimit', 'timeLimitMs']) &&
        isText(p.levelId, 64) && isText(p.levelVersion, 64) && isText(p.rulesVersion, 64) &&
        isUint(p.seed, 0xffffffff) && optUint(p.moveLimit, LIMITS.moveLimit) && optUint(p.timeLimitMs, LIMITS.timeLimitMs)
    case HOST.attemptStartRejected:
      return m.attemptId === undefined && only(p, ['levelId', 'reasonCode']) && isText(p.levelId, 64) && isText(p.reasonCode, 64)
    case HOST.attemptResumed:
    case HOST.attemptFinalized:
      return isText(m.attemptId) && Object.keys(p).length === 0
    default:
      return false
  }
}

/** Why an outgoing message is invalid, or null. The bridge validates before posting (guide §9). */
export function gameMessageProblem(m: Envelope): string | null {
  if (!only(m as unknown as Record<string, unknown>, ENVELOPE_KEYS)) return 'unknown envelope key'
  if (m.protocolVersion !== PROTOCOL_VERSION || !isGameId(m.gameId) || !isText(m.gameVersion, 32) || !isText(m.messageId)) return 'bad envelope'
  if (m.occurredAt !== undefined && Number.isNaN(Date.parse(m.occurredAt))) return 'bad occurredAt'
  if (!isObj(m.payload)) return 'payload must be an object'
  const p = m.payload as Record<string, unknown>
  const unbound = m.messageType === GAME.ready
  if (unbound ? m.sessionId !== undefined || m.nonce !== undefined || m.attemptId !== undefined : !isText(m.sessionId) || !isText(m.nonce)) {
    return 'session binding'
  }
  const attemptBound = !([GAME.ready, GAME.sessionReady, GAME.attemptStartRequested, GAME.closeRequested, GAME.fullscreenRequested, GAME.error] as string[]).includes(m.messageType)
  if (attemptBound && !isText(m.attemptId)) return 'missing attemptId'
  // close, fullscreen and error are session-bound; the attempt id is optional on them (it is informative when one is active)
  if (!attemptBound && !([GAME.closeRequested, GAME.fullscreenRequested, GAME.error] as string[]).includes(m.messageType) && m.attemptId !== undefined) return 'unexpected attemptId'
  switch (m.messageType) {
    case GAME.ready:
      return only(p, ['capabilities']) && Array.isArray(p.capabilities) && p.capabilities.every((c) => isText(c, 64)) ? null : 'bad payload'
    case GAME.sessionReady:
    case GAME.closeRequested:
    case GAME.fullscreenRequested:
      return Object.keys(p).length === 0 ? null : 'bad payload'
    case GAME.error:
      return only(p, ['code', 'detail']) && isText(p.code, 128) &&
        (p.detail === undefined || (typeof p.detail === 'string' && p.detail.length <= 1024)) ? null : 'bad payload'
    case GAME.attemptStartRequested:
      return only(p, ['levelId']) && isText(p.levelId, 64) ? null : 'bad payload'
    case GAME.action:
      return extrasOk(p) &&
        Number.isSafeInteger(p.sequence) && (p.sequence as number) >= 1 && isText(p.type, 64) &&
        optInt(p.fromX) && optInt(p.fromY) && optInt(p.toX) && optInt(p.toY) && optText(p.booster) && optUint(p.elapsedMs) &&
        (p.previousHash === '' || (typeof p.previousHash === 'string' && HEX64.test(p.previousHash))) &&
        typeof p.hash === 'string' && HEX64.test(p.hash) ? null : 'bad payload'
    case GAME.checkpoint:
      return only(p, ['attemptId', 'levelId', 'levelVersion', 'actionSequence', 'boardState', 'randomState', 'movesRemaining',
        'score', 'goals', 'boosters', 'restorable', 'hash']) && p.attemptId === m.attemptId && isText(p.levelId, 64) &&
        isText(p.levelVersion, 64) && isUint(p.actionSequence) && typeof p.boardState === 'string' && p.boardState.length <= LIMITS.boardState &&
        typeof p.randomState === 'string' && p.randomState.length <= LIMITS.randomState && isUint(p.movesRemaining) && Number.isFinite(p.score) &&
        typeof p.goals === 'string' && p.goals.length <= LIMITS.goals && (p.boosters === undefined || (typeof p.boosters === 'string' && p.boosters.length <= LIMITS.boosters)) &&
        typeof p.restorable === 'boolean' && typeof p.hash === 'string' && HEX64.test(p.hash) ? null : 'bad payload'
    case GAME.attemptSuspended:
      return only(p, ['reason']) && isText(p.reason, 128) ? null : 'bad payload'
    case GAME.attemptCompleted:
    case GAME.attemptFailed:
      return only(p, ['levelId', 'actionSequence', 'actionHash', 'checkpointHash']) && isText(p.levelId, 64) &&
        isUint(p.actionSequence) && (p.actionHash === '' || (typeof p.actionHash === 'string' && HEX64.test(p.actionHash))) &&
        (p.checkpointHash === undefined || (typeof p.checkpointHash === 'string' && HEX64.test(p.checkpointHash))) ? null : 'bad payload'
    default:
      return 'unknown messageType'
  }
}
